import React, { useState, useEffect } from 'react'
import {
  BanknotesIcon,
  DocumentTextIcon,
  ArrowTrendingUpIcon,
  BuildingLibraryIcon,
  CalendarDaysIcon,
  ExclamationTriangleIcon
} from '@heroicons/react/24/outline'
import { useAuthStore } from '@/stores/authStore'
import { API_BASE_URL } from '@/services/api'

interface DashboardData {
  contas_pagar: {
    total_pendentes: number
    total_vencidas: number
    valor_total_pendente: number
    proximos_7_dias: number
  }
  contas_receber: {
    total_pendentes: number
    total_vencidas: number
    valor_total_pendente: number
    proximos_7_dias: number
  }
  transferencias: {
    total_mes: number
    valor_total: number
  }
  saldo_contas: Array<{
    id: number
    nome: string
    tipo: string
    saldo: number
    moeda: string
  }>
  proximos_vencimentos: Array<{
    descricao: string
    valor: number
    data_vencimento: string
    tipo: string
  }>
}

const FinanceiroDashboard: React.FC = () => {
  const [data, setData] = useState<DashboardData | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const { token } = useAuthStore()

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/financeiro/dashboard`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        })

        if (!response.ok) {
          throw new Error('Erro ao carregar dados')
        }

        const result = await response.json()
        setData(result)
      } catch (err: any) {
        setError(err.message || 'Erro ao carregar dados')
      } finally {
        setLoading(false)
      }
    }

    fetchData()
  }, [])

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value)
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-xl p-6">
        <h3 className="text-red-800 font-semibold mb-2">Erro ao carregar dados</h3>
        <p className="text-red-600">{error}</p>
      </div>
    )
  }

  if (!data) return null

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-600 to-purple-600 rounded-xl p-6 text-white">
        <h1 className="text-2xl font-bold mb-2">Dashboard Financeiro Unificado</h1>
        <p className="text-blue-100">
          Visão geral completa das suas finanças em tempo real
        </p>
      </div>

      {/* Cards de Resumo */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {/* Contas a Pagar */}
        <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-sm font-medium text-gray-500">Contas a Pagar</h3>
              <p className="text-2xl font-bold text-gray-900">
                {formatCurrency(data.contas_pagar.valor_total_pendente)}
              </p>
              <p className="text-sm text-gray-600">
                {data.contas_pagar.total_pendentes} pendentes
              </p>
            </div>
            <DocumentTextIcon className="w-8 h-8 text-red-500" />
          </div>
          {data.contas_pagar.total_vencidas > 0 && (
            <div className="mt-4 flex items-center text-red-600">
              <ExclamationTriangleIcon className="w-4 h-4 mr-1" />
              <span className="text-xs">{data.contas_pagar.total_vencidas} vencidas</span>
            </div>
          )}
        </div>

        {/* Contas a Receber */}
        <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-sm font-medium text-gray-500">Contas a Receber</h3>
              <p className="text-2xl font-bold text-gray-900">
                {formatCurrency(data.contas_receber.valor_total_pendente)}
              </p>
              <p className="text-sm text-gray-600">
                {data.contas_receber.total_pendentes} pendentes
              </p>
            </div>
            <BanknotesIcon className="w-8 h-8 text-green-500" />
          </div>
          {data.contas_receber.total_vencidas > 0 && (
            <div className="mt-4 flex items-center text-red-600">
              <ExclamationTriangleIcon className="w-4 h-4 mr-1" />
              <span className="text-xs">{data.contas_receber.total_vencidas} vencidas</span>
            </div>
          )}
        </div>

        {/* Transferências */}
        <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-sm font-medium text-gray-500">Transferências</h3>
              <p className="text-2xl font-bold text-gray-900">
                {formatCurrency(data.transferencias.valor_total)}
              </p>
              <p className="text-sm text-gray-600">
                {data.transferencias.total_mes} este mês
              </p>
            </div>
            <ArrowTrendingUpIcon className="w-8 h-8 text-blue-500" />
          </div>
        </div>

        {/* Próximos Vencimentos */}
        <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-sm font-medium text-gray-500">Próximos 7 Dias</h3>
              <p className="text-2xl font-bold text-gray-900">
                {data.contas_pagar.proximos_7_dias + data.contas_receber.proximos_7_dias}
              </p>
              <p className="text-sm text-gray-600">vencimentos</p>
            </div>
            <CalendarDaysIcon className="w-8 h-8 text-orange-500" />
          </div>
        </div>
      </div>

      {/* Saldos das Contas */}
      {data.saldo_contas.length > 0 && (
        <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm">
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
            <BuildingLibraryIcon className="w-5 h-5 mr-2" />
            Saldo das Contas Bancárias
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {data.saldo_contas.map((conta, index) => (
              <div key={index} className="bg-gray-50 rounded-lg p-4">
                <p className="text-sm font-medium text-gray-600">{conta.nome}</p>
                <p className={`text-xl font-bold ${(conta.saldo || 0) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                  {formatCurrency(conta.saldo || 0)}
                </p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Próximos Vencimentos */}
      {data.proximos_vencimentos.length > 0 && (
        <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm">
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
            <CalendarDaysIcon className="w-5 h-5 mr-2" />
            Próximos Vencimentos
          </h3>
          <div className="space-y-3">
            {data.proximos_vencimentos.map((item, index) => (
              <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <div>
                  <p className="font-medium text-gray-900">{item.descricao}</p>
                  <p className="text-sm text-gray-500">
                    {new Date(item.data_vencimento).toLocaleDateString('pt-BR')} • {item.tipo}
                  </p>
                </div>
                <span className={`font-semibold ${
                  item.tipo === 'PAGAR' ? 'text-red-600' : 'text-green-600'
                }`}>
                  {item.tipo === 'PAGAR' ? '-' : '+'}
                  {formatCurrency(Math.abs(item.valor))}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Estado Vazio */}
      {data.saldo_contas.length === 0 && data.proximos_vencimentos.length === 0 && (
        <div className="bg-white rounded-xl border border-gray-200 p-8 text-center">
          <BuildingLibraryIcon className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">Nenhum dado encontrado</h3>
          <p className="text-gray-500">
            Adicione contas bancárias e transações para visualizar o dashboard financeiro.
          </p>
        </div>
      )}
    </div>
  )
}

export default FinanceiroDashboard